/**
 * yc-music.js
 * 统一音乐播放运行时，供全局与主播放器组件共用
 */

(function () {
  if (window.YcMusicRuntime) return;

  const SESSION_KEY = 'yc-music-state';
  let savedState = null;
  try {
    const savedStateStr = sessionStorage.getItem(SESSION_KEY);
    if (savedStateStr) savedState = JSON.parse(savedStateStr);
  } catch(e){}

  const state = savedState || {
    isPlaying: false,
    currentRecommendId: null,
    currentRecommendUrl: null,
    recommendCurrentTime: 0,
    recommendLoading: false,
    mixVolumes: {},
    mixUrls: {},
    mixLoading: {},
    mixInfos: {},
    mixCurrentTimes: {},
    listeningMode: false,
    currentTrackInfo: null,
    sleepTimerActive: false,
    sleepTimerRemaining: 0,
    hasStarted: false,
    isGlobalPlayerVisible: false
  };

  if (typeof state.isGlobalPlayerVisible !== 'boolean') {
    state.isGlobalPlayerVisible = false;
  }

  // Reset transient states
  state.recommendLoading = false;
  state.mixLoading = {};
  state.sleepTimerActive = false;
  state.sleepTimerRemaining = 0;
  state.listeningMode = false;

  const listeners = new Set();
  const audioInstances = {}; // { [id]: HTMLAudioElement }
  let recommendAudio = null;
  let sleepTimerInterval = null;
  let rotationInterval = null;
  let currentRotationIndex = 0;

  function saveState() {
    if (recommendAudio) {
      state.recommendCurrentTime = recommendAudio.currentTime;
    }
    Object.keys(audioInstances).forEach(id => {
      state.mixCurrentTimes[id] = audioInstances[id].currentTime;
    });

    sessionStorage.setItem(SESSION_KEY, JSON.stringify({
      ...state,
      recommendLoading: false,
      mixLoading: {},
      listeningMode: false
    }));
  }

  window.addEventListener('beforeunload', saveState);

  function notify() {
    saveState(); // sync to session storage
    listeners.forEach(fn => fn({ ...state }));
  }

  function markGlobalPlayerVisible(context = {}) {
    if (context.playerScope === 'section') {
      state.isGlobalPlayerVisible = true;
    }
  }

  function getAudio(id, url) {
    if (!audioInstances[id]) {
      const audio = new Audio(url);
      audio.loop = true;
      audio.crossOrigin = "anonymous";
      audio.preload = "none"; // 延迟加载，防止一次性加载导致卡死
      audio.addEventListener('waiting', () => {
        state.mixLoading[id] = true;
        notify();
      });
      audio.addEventListener('canplay', () => {
        state.mixLoading[id] = false;
        notify();
      });
      audio.addEventListener('playing', () => {
        state.mixLoading[id] = false;
        notify();
      });
      audioInstances[id] = audio;
    }
    return audioInstances[id];
  }

  function bindRecommendEvents() {
    recommendAudio.addEventListener('waiting', () => {
      state.recommendLoading = true;
      notify();
    });
    recommendAudio.addEventListener('canplay', () => {
      state.recommendLoading = false;
      notify();
    });
    recommendAudio.addEventListener('playing', () => {
      state.recommendLoading = false;
      notify();
    });
  }

  function updateListeningModeRotation() {
    if (!state.listeningMode) {
      if (rotationInterval) { clearInterval(rotationInterval); rotationInterval = null; }
      return;
    }

    const activeMixIds = Object.keys(state.mixVolumes).filter(id => state.mixVolumes[id] > 0);
    
    if (activeMixIds.length > 0 && !state.currentRecommendId) {
      // It's mix audio
      if (!rotationInterval) {
        rotationInterval = setInterval(() => {
          const activeMixIdsNow = Object.keys(state.mixVolumes).filter(id => state.mixVolumes[id] > 0);
          if (activeMixIdsNow.length === 0) return;
          currentRotationIndex = (currentRotationIndex + 1) % activeMixIdsNow.length;
          const mixId = activeMixIdsNow[currentRotationIndex];
          state.currentTrackInfo = state.mixInfos[mixId];
          notify();
        }, 8000);
      }
      // Set initial
      if (!state.currentTrackInfo || !activeMixIds.includes(Object.keys(state.mixInfos).find(k => state.mixInfos[k] === state.currentTrackInfo))) {
        currentRotationIndex = 0;
        state.currentTrackInfo = state.mixInfos[activeMixIds[0]];
      }
    } else {
      // Recommend audio
      if (rotationInterval) { clearInterval(rotationInterval); rotationInterval = null; }
    }
  }

  // Restore audio instances
  if (state.currentRecommendId && state.currentRecommendUrl) {
    recommendAudio = new Audio(state.currentRecommendUrl);
    recommendAudio.loop = true;
    recommendAudio.crossOrigin = "anonymous";
    recommendAudio.preload = "none";
    recommendAudio.currentTime = state.recommendCurrentTime || 0;
    bindRecommendEvents();
  }

  Object.keys(state.mixVolumes).forEach(id => {
    if (state.mixVolumes[id] > 0 && state.mixUrls[id]) {
      const audio = getAudio(id, state.mixUrls[id]);
      audio.volume = state.mixVolumes[id];
      audio.currentTime = state.mixCurrentTimes[id] || 0;
    }
  });

  // Check if we should really resume playing
  // and whether we should consider it 'hasStarted' based on current volumes
  const activeMixIds = Object.keys(state.mixVolumes).filter(id => state.mixVolumes[id] > 0);
  if (!state.currentRecommendId && activeMixIds.length === 0) {
    state.isPlaying = false;
    state.hasStarted = false;
  }

  if (state.isPlaying) {
    const playPromises = [];
    if (recommendAudio) playPromises.push(recommendAudio.play());
    Object.keys(audioInstances).forEach(id => {
      if (state.mixVolumes[id] > 0) {
        playPromises.push(audioInstances[id].play());
      }
    });

    Promise.all(playPromises).catch(e => {
      console.warn('Autoplay prevented', e);
      state.isPlaying = false;
      notify();
    });
  }

  window.YcMusicRuntime = {
    getState() {
      return { ...state };
    },

    subscribe(fn) {
      listeners.add(fn);
      fn({ ...state });
      return () => listeners.delete(fn);
    },

    playRecommend(id, url, info, context = {}) {
      console.log('[YcMusic] playRecommend clicked', { id, url, info, context });
      markGlobalPlayerVisible(context);
      if (state.currentRecommendId === id) {
        this.togglePlay(context);
        return;
      }

      if (recommendAudio) {
        recommendAudio.pause();
      }

      // Stop mix audio
      Object.keys(state.mixVolumes).forEach(mixId => {
        state.mixVolumes[mixId] = 0;
        if (audioInstances[mixId]) audioInstances[mixId].volume = 0;
      });

      state.currentRecommendId = id;
      state.currentRecommendUrl = url;
      state.isPlaying = true;
      state.hasStarted = true;
      if (info) state.currentTrackInfo = info;

      if (url) {
        recommendAudio = new Audio(url);
        recommendAudio.loop = true;
        recommendAudio.crossOrigin = "anonymous";
        recommendAudio.preload = "none"; // 延迟加载推荐音频
        bindRecommendEvents();
        recommendAudio.play().catch(e => console.error('Play recommend failed', e));
      }
      
      updateListeningModeRotation();
      notify();
      this.trackEvent('recommend_track_played', { recommendId: id, ...context });
    },

    setMixVolume(id, url, volume, info, context = {}) {
      console.log('[YcMusic] setMixVolume changed', { id, url, volume, info, context });
      if (volume > 0) {
        markGlobalPlayerVisible(context);
      }
      const oldVolume = state.mixVolumes[id] || 0;
      state.mixVolumes[id] = volume;
      state.mixUrls[id] = url;
      if (info) state.mixInfos[id] = info;
      
      // Stop recommend audio
      if (volume > 0 && state.currentRecommendId) {
        if (recommendAudio) recommendAudio.pause();
        state.currentRecommendId = null;
        state.currentRecommendUrl = null;
        state.currentTrackInfo = null;
        state.recommendLoading = false;
      }

      if (volume > 0 && info) {
        state.currentTrackInfo = info;
      }

      const audio = getAudio(id, url);
      audio.volume = volume;

      if (volume > 0) {
        state.hasStarted = true;
        if (!state.isPlaying) {
          state.isPlaying = true;
          
          // 当从暂停到播放时，同步所有激活音轨到 0
          Object.keys(audioInstances).forEach(otherId => {
            if (state.mixVolumes[otherId] > 0) {
              audioInstances[otherId].currentTime = 0;
            }
          });

          Object.keys(audioInstances).forEach(otherId => {
            if (state.mixVolumes[otherId] > 0 && otherId !== id) {
              audioInstances[otherId].play().catch(e => console.error(e));
            }
          });
        } else {
          // 如果当前已经在播放组合音频，并且有其他音轨处于激活状态，
          // 新拖动（之前音量为0）的音轨或者正在调整的音轨，为了保证完全同步，
          // 我们将所有当前激活的音轨（包括刚刚加入的）的 currentTime 统一重置为 0。
          const activeMixIds = Object.keys(state.mixVolumes).filter(k => state.mixVolumes[k] > 0);
          if (activeMixIds.length > 1) {
            activeMixIds.forEach(activeId => {
              if (audioInstances[activeId]) {
                audioInstances[activeId].currentTime = 0;
              }
            });
          }
        }

        if (audio.paused && state.isPlaying) {
          state.mixLoading[id] = true;
          notify();
          audio.play().catch(e => console.error('Play mix failed', e));
        }
      } else {
        audio.pause();
        state.mixLoading[id] = false;
      }
      
      updateListeningModeRotation();
      notify();
      
      if (oldVolume === 0 && volume > 0) {
        this.trackEvent('mix_track_activated', { mixTrackId: id, ...context });
      }
    },

    togglePlay(context = {}) {
      state.isPlaying = !state.isPlaying;
      
      if (state.isPlaying) {
        if (recommendAudio && state.currentRecommendId) recommendAudio.play().catch(e => console.error(e));
        Object.keys(audioInstances).forEach(id => {
          if (state.mixVolumes[id] > 0) {
            audioInstances[id].play().catch(e => console.error(e));
          }
        });
      } else {
        if (recommendAudio) recommendAudio.pause();
        Object.keys(audioInstances).forEach(id => {
          audioInstances[id].pause();
        });
      }
      notify();
    },

    enterListeningMode(context = {}) {
      state.listeningMode = true;
      updateListeningModeRotation();
      notify();
    },

    exitListeningMode(context = {}) {
      state.listeningMode = false;
      updateListeningModeRotation();
      notify();
    },

    setSleepTimer(minutes) {
      if (sleepTimerInterval) {
        clearInterval(sleepTimerInterval);
        sleepTimerInterval = null;
      }
      if (minutes > 0) {
        state.sleepTimerActive = true;
        state.sleepTimerRemaining = minutes * 60;
        sleepTimerInterval = setInterval(() => {
          if (state.isPlaying) {
            state.sleepTimerRemaining -= 1;
            if (state.sleepTimerRemaining <= 0) {
              clearInterval(sleepTimerInterval);
              sleepTimerInterval = null;
              state.sleepTimerActive = false;
              state.sleepTimerRemaining = 0;
              if (state.isPlaying) {
                this.togglePlay();
              }
            }
            notify();
          }
        }, 1000);
      } else {
        state.sleepTimerActive = false;
        state.sleepTimerRemaining = 0;
      }
      notify();
    },

    async mergeMixAudio(mixes) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) throw new Error('Web Audio API not supported');
      const audioContext = new AudioContextClass();
      
      const buffers = await Promise.all(mixes.map(async (mix) => {
        const response = await fetch(mix.url);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        return { buffer: audioBuffer, volume: mix.volume };
      }));

      const maxDuration = Math.max(...buffers.map(b => b.buffer.duration));
      const offlineContext = new OfflineAudioContext(2, audioContext.sampleRate * maxDuration, audioContext.sampleRate);
      
      buffers.forEach(({ buffer, volume }) => {
        const source = offlineContext.createBufferSource();
        source.buffer = buffer;
        const gainNode = offlineContext.createGain();
        gainNode.gain.value = volume;
        source.connect(gainNode);
        gainNode.connect(offlineContext.destination);
        source.start(0);
      });

      const renderedBuffer = await offlineContext.startRendering();
      
      return this.bufferToWav(renderedBuffer);
    },

    bufferToWav(abuffer) {
      let numOfChan = abuffer.numberOfChannels,
          length = abuffer.length * numOfChan * 2 + 44,
          buffer = new ArrayBuffer(length),
          view = new DataView(buffer),
          channels = [], i, sample,
          offset = 0,
          pos = 0;

      function setUint16(data) { view.setUint16(offset, data, true); offset += 2; }
      function setUint32(data) { view.setUint32(offset, data, true); offset += 4; }

      setUint32(0x46464952);
      setUint32(length - 8);
      setUint32(0x45564157);
      setUint32(0x20746d66);
      setUint32(16);
      setUint16(1);
      setUint16(numOfChan);
      setUint32(abuffer.sampleRate);
      setUint32(abuffer.sampleRate * 2 * numOfChan);
      setUint16(numOfChan * 2);
      setUint16(16);
      setUint32(0x61746164);
      setUint32(length - pos - 4);

      for(i = 0; i < abuffer.numberOfChannels; i++)
        channels.push(abuffer.getChannelData(i));

      while(pos < abuffer.length) {
        for(i = 0; i < numOfChan; i++) {
          sample = Math.max(-1, Math.min(1, channels[i][pos]));
          sample = (0.5 + sample < 0 ? sample * 32768 : sample * 32767)|0;
          view.setInt16(offset, sample, true);
          offset += 2;
        }
        pos++;
      }

      return new Blob([buffer], {type: "audio/wav"});
    },

    trackEvent(eventName, payload = {}) {
      this._trackedEvents = this._trackedEvents || new Set();
      const entityId = payload.recommendId || payload.mixTrackId || 'player';
      const trackKey = `${eventName}_${entityId}`;
      
      if (this._trackedEvents.has(trackKey)) {
        return; // 在当前页面生命周期内已经记录过，不再重复发送
      }
      this._trackedEvents.add(trackKey);

      const helper = window.YcAppProxy;
      const trackingUrl = '/apps/kailashenergy/tracking/events';

      const getStored = (store, key, prefix) => {
        if (helper?.getOrCreateStoredValue) return helper.getOrCreateStoredValue(store, key, prefix);
        try {
          let val = store.getItem(key);
          if (!val) {
            val = `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
            store.setItem(key, val);
          }
          return val;
        } catch(e) { return `${prefix}-fallback`; }
      };

      const visitorId = getStored(window.localStorage, 'yc-music-visitor-id', 'visitor');
      const sessionId = getStored(window.sessionStorage, 'yc-music-session-id', 'session');

      const detail = {
        entityType: 'music_player',
        entityId: payload.recommendId || payload.mixTrackId || 'player',
        eventPayload: {
          ...payload,
          pageUrl: window.location.href,
        }
      };

      if (helper?.trackEvent) {
        helper.trackEvent({
          moduleKey: 'yc-music',
          appModule: 'music',
          eventName,
          detail,
          page: { keepalive: true },
          url: trackingUrl
        }).catch(() => {});
        return;
      }

      const fetchPayload = {
        appModule: 'music',
        eventName,
        visitorId,
        sessionId,
        pageUrl: window.location.href,
        occurredAt: new Date().toISOString(),
        ...detail
      };

      fetch(trackingUrl, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(fetchPayload),
      }).catch(() => {});
    }
  };
})();

/**
 * Custom Element: yc-music
 */
if (!customElements.get('yc-music')) {
  class YcMusic extends HTMLElement {
    connectedCallback() {
      setTimeout(() => {
        this.settings = JSON.parse(this.getAttribute('data-settings') || '{}');
        this.unsubscribe = window.YcMusicRuntime.subscribe(this.render.bind(this));
        this.initEvents();
      }, 0);
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._onDocumentClick) document.removeEventListener('click', this._onDocumentClick);
    }

    initEvents() {
      // Recommend Plays
      this.addEventListener('click', (e) => {
        const recommendBtn = e.target.closest('.js-play-recommend');
        if (recommendBtn) {
          const id = recommendBtn.getAttribute('data-id');
          const url = recommendBtn.getAttribute('data-url');
          const info = {
            title: recommendBtn.getAttribute('data-title'),
            desc: recommendBtn.getAttribute('data-desc'),
            image: recommendBtn.getAttribute('data-image'),
            layers: recommendBtn.getAttribute('data-layers'),
            duration: recommendBtn.getAttribute('data-duration')
          };
          const context = { playerScope: 'section', sourceSectionId: this.getAttribute('data-section-id') || 'unknown' };
          window.YcMusicRuntime.playRecommend(id, url, info, context);
        }

        const enterListeningBtn = e.target.closest('.js-enter-listening');
        if (enterListeningBtn) {
          e.stopPropagation();
          e.preventDefault();
          const context = { playerScope: 'section', sourceSectionId: this.getAttribute('data-section-id') || 'unknown' };
          window.YcMusicRuntime.enterListeningMode(context);
        }
      });

      // Mix Volumes (使用事件代理)
      this.addEventListener('input', (e) => {
        if (e.target.classList.contains('js-mix-slider')) {
          const slider = e.target;
          const id = slider.getAttribute('data-id');
          const url = slider.getAttribute('data-url');
          const vol = parseFloat(slider.value) / 100;
          slider.style.setProperty('--value', slider.value + '%');
          const info = {
            title: slider.getAttribute('data-title'),
            desc: slider.getAttribute('data-desc'),
            image: slider.getAttribute('data-image')
          };
          const context = { playerScope: 'section', sourceSectionId: this.getAttribute('data-section-id') || 'unknown' };
          window.YcMusicRuntime.setMixVolume(id, url, vol, info, context);
        }
      });

      // Sleep Timer
      this.addEventListener('click', (e) => {
        const timerToggleBtn = e.target.closest('.js-timer-toggle');
        if (timerToggleBtn) {
          e.stopPropagation();
          const wrapper = timerToggleBtn.closest('.js-timer-wrapper');
          const menu = wrapper.querySelector('.js-timer-menu');
          const isMenuVisible = menu.style.display === 'flex';
          
          document.querySelectorAll('.js-timer-menu').forEach(m => m.style.display = 'none');
          
          if (!isMenuVisible) {
            menu.style.display = 'flex';
          }
        }

        const timerOption = e.target.closest('.js-timer-option');
        if (timerOption) {
          e.stopPropagation();
          const minutes = parseInt(timerOption.getAttribute('data-time'), 10);
          window.YcMusicRuntime.setSleepTimer(minutes);
          const menu = timerOption.closest('.js-timer-menu');
          if (menu) menu.style.display = 'none';

          // 选择后展开全局播放器
          const globalPlayer = document.querySelector('.yc-music-global');
          if (globalPlayer && globalPlayer.classList.contains('is-collapsed')) {
            globalPlayer.classList.remove('is-collapsed');
            globalPlayer.classList.add('is-expanded');
            
            // 手动触发一次吸附计算以确保定位正确
            if (globalPlayer.dataset.dragged !== 'true') {
               globalPlayer.style.right = '20px';
               globalPlayer.classList.add('is-snapped-right');
               globalPlayer.classList.remove('is-snapped-left');
            } else {
               const rect = globalPlayer.getBoundingClientRect();
               const viewportWidth = window.innerWidth;
               if (globalPlayer.classList.contains('is-snapped-right')) {
                 globalPlayer.style.left = (viewportWidth - rect.width - 20) + 'px';
               }
            }
          }
        }
      });

      // Action Buttons (Save This Mix & Share)
      this.addEventListener('click', async (e) => {
        const saveBtn = e.target.closest('.js-save-mix');
        const shareBtn = e.target.closest('.js-share-mix');
        
        if (!saveBtn && !shareBtn) return;
        
        const state = window.YcMusicRuntime.getState();
        const activeMixIds = Object.keys(state.mixVolumes).filter(id => state.mixVolumes[id] > 0);
        
        if (!state.currentRecommendId && activeMixIds.length === 0) {
          alert('No audio is currently playing.');
          return;
        }

        const btn = saveBtn || shareBtn;
        const iconWrap = btn.querySelector('.js-icon');
        const textWrap = btn.querySelector('.js-text');
        
        const originalIcon = iconWrap.outerHTML;
        const originalText = textWrap.textContent;
        const actionType = saveBtn ? 'save' : 'share';

        // Set Loading State
        btn.style.pointerEvents = 'none';
        iconWrap.outerHTML = `
          <svg class="icon-loading js-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="2" x2="12" y2="6"></line>
            <line x1="12" y1="18" x2="12" y2="22"></line>
            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
            <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
            <line x1="2" y1="12" x2="6" y2="12"></line>
            <line x1="18" y1="12" x2="22" y2="12"></line>
            <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
            <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
          </svg>
        `;
        textWrap.textContent = actionType === 'save' ? 'Downloading...' : 'Preparing...';

        try {
          let downloadUrl = '';
          let filename = '';

          if (state.currentRecommendId) {
            downloadUrl = state.currentRecommendUrl;
            filename = (state.currentTrackInfo?.title || 'kailash-recommend') + '.mp3';
          } else {
            const mixes = activeMixIds.map(id => ({ url: state.mixUrls[id], volume: state.mixVolumes[id] }));
            const wavBlob = await window.YcMusicRuntime.mergeMixAudio(mixes);
            downloadUrl = URL.createObjectURL(wavBlob);
            filename = 'kailash-mix.wav';
          }

          if (actionType === 'save') {
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            if (!state.currentRecommendId) URL.revokeObjectURL(downloadUrl);
          } else {
            // Share
            if (navigator.share) {
              let shareData = {
                title: 'Kailash Energy Music',
                text: 'Listen to my Kailash Energy Mix!'
              };
              
              // 统一获取音频 blob 并添加到分享文件列表中
              const blob = await fetch(downloadUrl).then(r => r.blob());
              const fileType = state.currentRecommendId ? 'audio/mpeg' : 'audio/wav';
              const file = new File([blob], filename, { type: fileType });
              
              if (navigator.canShare && navigator.canShare({ files: [file] })) {
                shareData.files = [file];
              }
              
              await navigator.share(shareData);
            } else {
              alert('Sharing is not supported on this browser.');
            }
          }

        } catch (err) {
          console.error('Action failed:', err);
          alert('An error occurred. Please try again.');
        } finally {
          btn.style.pointerEvents = 'auto';
          const newIconWrap = btn.querySelector('.js-icon');
          if (newIconWrap) newIconWrap.outerHTML = originalIcon;
          textWrap.textContent = originalText;
        }
      });
    }

    render(state) {
      // Sync Recommend buttons
      this.querySelectorAll('.js-play-recommend').forEach(btn => {
        const id = btn.getAttribute('data-id');
        const isCurrent = state.currentRecommendId === id;
        const isPlaying = isCurrent && state.isPlaying;
        const isLoading = isPlaying && state.recommendLoading;

        if (isLoading) {
          btn.classList.remove('is-playing');
          btn.innerHTML = `
            <svg class="icon-loading" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="2" x2="12" y2="6"></line>
              <line x1="12" y1="18" x2="12" y2="22"></line>
              <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
              <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
              <line x1="2" y1="12" x2="6" y2="12"></line>
              <line x1="18" y1="12" x2="22" y2="12"></line>
              <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
              <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
            </svg>
            Loading
          `;
        } else if (isPlaying) {
          btn.classList.add('is-playing');
          btn.innerHTML = `
            <svg class="icon-pause" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="6" y="4" width="4" height="16"></rect>
              <rect x="14" y="4" width="4" height="16"></rect>
            </svg>
            Pause Mix
          `;
        } else {
          btn.classList.remove('is-playing');
          btn.innerHTML = `
            <svg class="icon-play" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
            Play Mix
          `;
        }
      });

      // Sync Mix States (Loading/Playing)
      this.querySelectorAll('.yc-music-mix-item').forEach(item => {
        const id = item.getAttribute('data-id');
        const volume = state.mixVolumes[id] || 0;
        const isPlaying = state.isPlaying && volume > 0;
        const isLoading = isPlaying && state.mixLoading[id];

        const iconWrap = item.querySelector('.yc-music-mix-item__icon');
        if (iconWrap) {
          if (isLoading) {
            iconWrap.innerHTML = `
              <svg class="icon-loading" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="2" x2="12" y2="6"></line>
                <line x1="12" y1="18" x2="12" y2="22"></line>
                <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                <line x1="2" y1="12" x2="6" y2="12"></line>
                <line x1="18" y1="12" x2="22" y2="12"></line>
                <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
              </svg>
            `;
          } else if (isPlaying) {
            iconWrap.innerHTML = `
              <svg class="icon-pause" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="6" y="4" width="4" height="16"></rect>
                <rect x="14" y="4" width="4" height="16"></rect>
              </svg>
            `;
          } else {
            iconWrap.innerHTML = `
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            `;
          }
        }

        if (isLoading) {
          item.classList.add('is-loading');
        } else {
          item.classList.remove('is-loading');
        }
        
        const slider = item.querySelector('.js-mix-slider');
        if (slider && state.mixVolumes[id] !== undefined) {
          const val = state.mixVolumes[id] * 100;
          slider.value = val;
          slider.style.setProperty('--value', val + '%');
        }
      });

      // Sync Sleep Timer UI
      const timerBtn = this.querySelector('.js-timer-toggle');
      const timerText = this.querySelector('.js-timer-text');
      const timerIcon = this.querySelector('.js-timer-icon');
      if (timerBtn && timerText && timerIcon) {
        if (state.sleepTimerActive) {
          timerBtn.classList.add('is-timer-active');
          const mins = Math.floor(state.sleepTimerRemaining / 60);
          const secs = state.sleepTimerRemaining % 60;
          
          if (this.tagName.toLowerCase() === 'yc-music') {
            // 主播放器（保留图标，仅更新文本）
            timerText.textContent = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
          } else {
            // 全局播放器（隐藏图标，显示文本）
            timerIcon.style.display = 'none';
            timerText.style.display = 'inline';
            timerText.textContent = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
          }
        } else {
          timerBtn.classList.remove('is-timer-active');
          
          if (this.tagName.toLowerCase() === 'yc-music') {
            // 主播放器（恢复原样文本）
            timerText.textContent = 'Sleep Timer';
          } else {
            // 全局播放器（恢复显示图标，隐藏文本）
            timerIcon.style.display = 'block';
            timerText.style.display = 'none';
          }
        }
      }

    }
  }
  customElements.define('yc-music', YcMusic);
}

/**
 * Custom Element: yc-music-global
 */
if (!customElements.get('yc-music-global')) {
  class YcMusicGlobal extends HTMLElement {
    connectedCallback() {
      setTimeout(() => {
        this.unsubscribe = window.YcMusicRuntime.subscribe(this.render.bind(this));
        this.initEvents();
      }, 0);
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    initEvents() {
      const el = this.querySelector('.yc-music-global');
      if (!el) return;

      this.addEventListener('click', (e) => {
        // 全局播放/暂停
        const toggleBtn = e.target.closest('.js-global-toggle');
        if (toggleBtn) {
          e.stopPropagation();
          const context = { playerScope: 'global' };
          window.YcMusicRuntime.togglePlay(context);
        }

        // 监听模式逻辑
        const enterListeningBtn = e.target.closest('.js-enter-listening');
        if (enterListeningBtn) {
          e.stopPropagation();
          e.preventDefault();
          const context = { playerScope: 'global' };
          window.YcMusicRuntime.enterListeningMode(context);
        }

        // 监听模式退出逻辑
        const exitBtn = e.target.closest('.js-exit-listening');
        if (exitBtn) {
          e.stopPropagation();
          e.preventDefault();
          const context = { playerScope: 'global' };
          window.YcMusicRuntime.exitListeningMode(context);
        }

        // 监听模式下的播放/暂停切换
        const listeningToggleBtn = e.target.closest('.js-listening-toggle');
        if (listeningToggleBtn) {
          e.stopPropagation();
          e.preventDefault();
          const context = { playerScope: 'global' };
          window.YcMusicRuntime.togglePlay(context);
        }

        // 展开/收起逻辑
        const toggleExpandBtn = e.target.closest('.js-toggle-expand');
        if (toggleExpandBtn) {
          e.stopPropagation();
          toggleCollapse(!el.classList.contains('is-collapsed'));
        }

        // 倒计时菜单逻辑
        const timerToggleBtn = e.target.closest('.js-timer-toggle');
        if (timerToggleBtn) {
          e.stopPropagation();
          const wrapper = timerToggleBtn.closest('.js-timer-wrapper');
          const menu = wrapper.querySelector('.js-timer-menu');
          const isMenuVisible = menu.style.display === 'flex';
          
          // 隐藏其他可能打开的菜单
          document.querySelectorAll('.js-timer-menu').forEach(m => m.style.display = 'none');
          
          if (!isMenuVisible) {
            menu.style.display = 'flex';
          }
        }

        // 选择倒计时选项逻辑
        const timerOption = e.target.closest('.js-timer-option');
        if (timerOption) {
          e.stopPropagation();
          const minutes = parseInt(timerOption.getAttribute('data-time'), 10);
          window.YcMusicRuntime.setSleepTimer(minutes);
          const menu = timerOption.closest('.js-timer-menu');
          if (menu) menu.style.display = 'none';
        }
      });

      // 拖拽与侧吸逻辑
      let isDragging = false;
      let startX = 0;
      let startY = 0;
      let initialLeft = 0;
      let initialTop = 0;

      const onPointerDown = (e) => {
        // 如果点击的是按钮等交互元素，不触发拖拽
        if (e.target.closest('button') || e.target.closest('a')) return;

        isDragging = true;
        el.classList.add('is-dragging');
        
        const rect = el.getBoundingClientRect();
        // 将基于 CSS 的定位转换为绝对的 left/top
        el.dataset.dragged = 'true';
        el.style.right = 'auto';
        el.style.bottom = 'auto';
        el.style.transform = 'none';
        el.style.left = rect.left + 'px';
        el.style.top = rect.top + 'px';

        startX = e.clientX;
        startY = e.clientY;
        initialLeft = rect.left;
        initialTop = rect.top;

        el.setPointerCapture(e.pointerId);
      };

      const onPointerMove = (e) => {
        if (!isDragging) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        let newLeft = initialLeft + dx;
        let newTop = initialTop + dy;

        el.style.left = newLeft + 'px';
        el.style.top = newTop + 'px';
      };

      const snapToEdge = () => {
        const rect = el.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const isCollapsed = el.classList.contains('is-collapsed');
        const paddingX = isCollapsed ? 0 : 20;
        const paddingY = 20;

        // 如果从未拖拽过，则保留 CSS 的居中定位，只处理 right 贴边
        if (!el.dataset.dragged) {
          el.style.right = paddingX + 'px';
          el.classList.add('is-snapped-right');
          el.classList.remove('is-snapped-left');
          return;
        }

        let finalTop = rect.top;
        if (finalTop < paddingY) {
          finalTop = paddingY;
        } else if (finalTop > viewportHeight - rect.height - paddingY) {
          finalTop = viewportHeight - rect.height - paddingY;
        }
        el.style.top = finalTop + 'px';

        // 左右侧吸
        if (rect.left + rect.width / 2 < viewportWidth / 2) {
          // 吸附到左侧
          el.style.left = paddingX + 'px';
          el.style.right = 'auto';
          el.classList.add('is-snapped-left');
          el.classList.remove('is-snapped-right');
        } else {
          // 吸附到右侧
          const hasLeft = el.style.left && el.style.left !== 'auto';
          if (hasLeft) {
            // 从拖拽状态通过 left 动画贴边
            el.style.left = (viewportWidth - rect.width - paddingX) + 'px';
            clearTimeout(el._snapRightTimeout);
            el._snapRightTimeout = setTimeout(() => {
              if (!isDragging && el.classList.contains('is-snapped-right')) {
                el.style.left = 'auto';
                el.style.right = (el.classList.contains('is-collapsed') ? 0 : 20) + 'px';
              }
            }, 350);
          } else {
            // 已经是右侧吸附状态，直接更新 right，依靠 CSS 自动适应宽度变化
            el.style.right = paddingX + 'px';
          }
          el.classList.add('is-snapped-right');
          el.classList.remove('is-snapped-left');
        }
      };

      const toggleCollapse = (willCollapse) => {
        if (willCollapse === el.classList.contains('is-collapsed')) return;
        
        if (willCollapse) {
          el.classList.add('is-collapsed');
          el.classList.remove('is-expanded');
        } else {
          el.classList.remove('is-collapsed');
          el.classList.add('is-expanded');
        }
        
        snapToEdge();
      };

      const onPointerUp = (e) => {
        if (!isDragging) return;
        isDragging = false;
        el.classList.remove('is-dragging');
        el.releasePointerCapture(e.pointerId);

        snapToEdge();
      };

      // 监听窗口大小变化以重新吸附
      const onResize = () => {
        if (!el.style.left || isDragging) return;
        snapToEdge();
      };

      el.addEventListener('pointerdown', onPointerDown);
      el.addEventListener('pointermove', onPointerMove);
      el.addEventListener('pointerup', onPointerUp);
      el.addEventListener('pointercancel', onPointerUp);
      window.addEventListener('resize', onResize);
      this._onResize = onResize; // 供清理

      // 全局点击失去焦点自动收起
      const onDocumentClick = (e) => {
        // 隐藏倒计时菜单
        document.querySelectorAll('.js-timer-menu').forEach(m => {
          if (!m.contains(e.target) && !e.target.closest('.js-timer-toggle')) {
            m.style.display = 'none';
          }
        });

        // 如果点击的是悬浮窗内部，或者悬浮窗已经是收起状态，则忽略
        if (el.contains(e.target) || el.classList.contains('is-collapsed')) return;
        
        toggleCollapse(true);
      };
      document.addEventListener('click', onDocumentClick);
      this._onDocumentClick = onDocumentClick;

      // 事件已合并到事件代理中
    }

    render(state) {
      const el = this.querySelector('.yc-music-global');
      if (!el) return;

      if (!state.isGlobalPlayerVisible) {
        el.style.display = 'none';
      } else {
        el.style.display = '';
      }

      // Sync Listening Mode
      const listeningEl = this.querySelector('.yc-music-listening-mode');
      if (listeningEl) {
        if (state.listeningMode) {
          listeningEl.classList.remove('hidden');
          
          const dateTextEl = listeningEl.querySelector('.js-listening-date');
          if (dateTextEl) {
            const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
            const now = new Date();
            dateTextEl.textContent = `DAILY STILLNESS · ${months[now.getMonth()]} ${now.getDate()}`;
          }

          const titleEl = listeningEl.querySelector('.js-listening-title');
          const descEl = listeningEl.querySelector('.js-listening-desc');
          const valueEl = listeningEl.querySelector('.js-listening-value');
          const activeBg = listeningEl.querySelector('.js-listening-bg-active');
          const fallbackBg = listeningEl.querySelector('.js-listening-bg-fallback');

          if (state.currentTrackInfo) {
            if (titleEl) titleEl.textContent = state.currentTrackInfo.title || 'Unknown Title';
            if (valueEl) valueEl.textContent = state.currentTrackInfo.desc || 'Unknown Artist';
            
            if (descEl) {
              let dynamicLayers = 0;
              if (state.currentRecommendId && state.currentTrackInfo) {
                dynamicLayers += parseInt(state.currentTrackInfo.layers) || 1;
              } else if (state.currentTrackInfo) {
                dynamicLayers += parseInt(state.currentTrackInfo.layers) || 0;
              }
              Object.keys(state.mixVolumes).forEach(id => {
                if (state.mixVolumes[id] > 0) dynamicLayers++;
              });

              let descText = [];
              if (dynamicLayers > 0) {
                descText.push(`${dynamicLayers} Layers`);
              }
              if (state.currentTrackInfo.duration) {
                descText.push(`${state.currentTrackInfo.duration} min`);
              }
              descEl.textContent = descText.join(' · ');
            }
            
            if (state.currentTrackInfo.image) {
              if (activeBg) {
                activeBg.src = state.currentTrackInfo.image;
                activeBg.style.display = 'block';
              }
              if (fallbackBg) fallbackBg.style.display = 'none';
            } else {
              if (activeBg) activeBg.style.display = 'none';
              if (fallbackBg) fallbackBg.style.display = 'block';
            }
          } else {
            if (titleEl) titleEl.textContent = 'Kailash Energy Default Title';
            if (valueEl) valueEl.textContent = 'Kailash Energy Default Description';
            if (descEl) descEl.textContent = '';
            if (activeBg) activeBg.style.display = 'none';
            if (fallbackBg) fallbackBg.style.display = 'block';
          }
        } else {
          listeningEl.classList.add('hidden');
        }
      }

      // Sync listening mode toggle button icon
      const listeningToggleBtn = this.querySelector('.js-listening-toggle');
      if (listeningToggleBtn) {
        const isAnyLoading = state.isPlaying && (
          state.recommendLoading || 
          Object.keys(state.mixVolumes).some(id => state.mixVolumes[id] > 0 && state.mixLoading[id])
        );

        if (isAnyLoading) {
          listeningToggleBtn.innerHTML = `
            <svg class="icon-loading" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="2" x2="12" y2="6"></line>
              <line x1="12" y1="18" x2="12" y2="22"></line>
              <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
              <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
              <line x1="2" y1="12" x2="6" y2="12"></line>
              <line x1="18" y1="12" x2="22" y2="12"></line>
              <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
              <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
            </svg>
          `;
        } else if (state.isPlaying) {
          listeningToggleBtn.innerHTML = `
            <svg class="icon-pause" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="6" y="4" width="4" height="16"></rect>
              <rect x="14" y="4" width="4" height="16"></rect>
            </svg>
          `;
        } else {
          listeningToggleBtn.innerHTML = `
            <svg class="icon-play" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
          `;
        }
      }

      // 计算当前播放层数与曲目名称
      let layersCount = 0;
      let trackNames = [];

      if (state.currentRecommendId && state.currentTrackInfo) {
         trackNames.push(state.currentTrackInfo.title);
         layersCount += parseInt(state.currentTrackInfo.layers) || 1;
      }

      Object.keys(state.mixVolumes).forEach(id => {
        if (state.mixVolumes[id] > 0) {
          layersCount++;
          if (state.mixInfos[id] && state.mixInfos[id].title) {
            trackNames.push(state.mixInfos[id].title);
          }
        }
      });

      const countEls = this.querySelectorAll('.js-layer-count');
      countEls.forEach(countEl => countEl.textContent = layersCount);

      const nameEls = this.querySelectorAll('.js-track-names');
      nameEls.forEach(nameEl => {
        if (trackNames.length > 0) {
          nameEl.textContent = trackNames.join(' · ');
        } else {
          nameEl.textContent = 'None';
        }
      });

      // Sync Sleep Timer UI
      const timerBtn = this.querySelector('.js-timer-toggle');
      const timerText = this.querySelector('.js-timer-text');
      const timerIcon = this.querySelector('.js-timer-icon');
      if (timerBtn && timerText && timerIcon) {
        if (state.sleepTimerActive) {
          timerBtn.classList.add('is-timer-active');
          const mins = Math.floor(state.sleepTimerRemaining / 60);
          const secs = state.sleepTimerRemaining % 60;
          
          if (this.tagName.toLowerCase() === 'yc-music') {
            // 主播放器（保留图标，仅更新文本）
            timerText.textContent = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
          } else {
            // 全局播放器（隐藏图标，显示文本）
            timerIcon.style.display = 'none';
            timerText.style.display = 'inline';
            timerText.textContent = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
          }
        } else {
          timerBtn.classList.remove('is-timer-active');
          
          if (this.tagName.toLowerCase() === 'yc-music') {
            // 主播放器（恢复原样文本）
            timerText.textContent = 'Sleep Timer';
          } else {
            // 全局播放器（恢复显示图标，隐藏文本）
            timerIcon.style.display = 'block';
            timerText.style.display = 'none';
          }
        }
      }

      // 渲染红点
      const dots = this.querySelectorAll('.yc-music-global__status-dot, .yc-music-global__status-dot--abs');
      dots.forEach(dot => {
        dot.style.display = 'block';
        dot.style.backgroundColor = state.isPlaying ? '#4caf50' : '#e65124';
      });

      // 更新播放/暂停图标
      const toggleBtns = this.querySelectorAll('.js-global-toggle');
      toggleBtns.forEach(btn => {
        let svg = btn.querySelector('svg');
        if (svg) {
          if (state.isPlaying) {
            svg.outerHTML = `
              <svg class="icon-pause" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <rect x="6" y="4" width="4" height="16"></rect>
                <rect x="14" y="4" width="4" height="16"></rect>
              </svg>
            `;
          } else {
            svg.outerHTML = `
              <svg class="icon-play" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
            `;
          }
        }
      });
    }
  }
  customElements.define('yc-music-global', YcMusicGlobal);
}
